import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { listContractors, createContractor, deleteContractor } from '../lib/api';
import { formatDate, getReputationBg } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, Eye, Users, AlertTriangle, Search, X, Filter, Building, MapPin } from 'lucide-react';
import api from '../lib/api';

const CONTRACTOR_TYPES = [
  { value: 'klient', label_pl: 'Klient / Odbiorca', label_en: 'Client / Buyer' },
  { value: 'dostawca', label_pl: 'Dostawca', label_en: 'Supplier' },
  { value: 'projektant', label_pl: 'Projektant', label_en: 'Designer' },
  { value: 'wykonawca', label_pl: 'Wykonawca', label_en: 'Contractor' },
  { value: 'partner', label_pl: 'Partner', label_en: 'Partner' },
];

export default function Contractors({ lang, tenantId }) {
  const [contractors, setContractors] = useState([]);
  const [totalContractors, setTotalContractors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', notes: '',
    contractor_type: '', industry: '', specialization: '', city: '', region: '', decision_role: '',
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    contractor_type: null, industry: null, city: null, decision_role: null,
  });
  const [filterOptions, setFilterOptions] = useState({
    types: [], industries: [], cities: [], decision_roles: [], tags: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadContractors(); }, [tenantId, search, filters]);
  useEffect(() => { loadFilterOptions(); }, [tenantId]);

  async function loadContractors() {
    setLoading(true);
    try {
      const params = { tenant_id: tenantId, limit: 200 };
      if (search) params.search = search;
      if (filters.contractor_type) params.contractor_type = filters.contractor_type;
      if (filters.industry) params.industry = filters.industry;
      if (filters.city) params.city = filters.city;
      if (filters.decision_role) params.decision_role = filters.decision_role;

      const res = await api.get('/contractors', { params }).then(r => r.data);
      setContractors(res.contractors || res);
      setTotalContractors(res.total ?? (res.contractors || res).length);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function loadFilterOptions() {
    try {
      const res = await api.get('/contractors/filters', { params: { tenant_id: tenantId } }).then(r => r.data);
      setFilterOptions(res);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleCreate() {
    if (!form.name.trim()) {
      toast.error(lang === 'pl' ? 'Nazwa jest wymagana' : 'Name is required');
      return;
    }
    try {
      await createContractor(form, tenantId);
      toast.success(lang === 'pl' ? 'Kontrahent dodany' : 'Contractor added');
      setDialogOpen(false);
      setForm({ name: '', company: '', email: '', phone: '', notes: '',
        contractor_type: '', industry: '', specialization: '', city: '', region: '', decision_role: '' });
      loadContractors();
      loadFilterOptions();
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd' : 'Error');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(lang === 'pl' ? 'Usunąć kontrahenta?' : 'Delete contractor?')) return;
    try {
      await deleteContractor(id);
      toast.success(lang === 'pl' ? 'Usunięto' : 'Deleted');
      loadContractors();
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd' : 'Error');
    }
  }

  function clearFilters() {
    setFilters({ contractor_type: null, industry: null, city: null, decision_role: null });
    setSearch('');
  }

  const hasActiveFilters = Object.values(filters).some(v => v) || search;
  const typeLabel = (type) => {
    const found = CONTRACTOR_TYPES.find(t => t.value === type);
    return found ? (lang === 'pl' ? found.label_pl : found.label_en) : type;
  };

  return (
    <div className="space-y-6" data-testid="contractors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {t('contractors', lang)}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totalContractors} {lang === 'pl' ? 'kontrahentów' : 'contractors'}
            {hasActiveFilters && <span className="text-primary ml-1">({lang === 'pl' ? 'filtrowane' : 'filtered'})</span>}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-contractor-button">
              <Plus className="w-4 h-4" />
              {t('addContractor', lang)}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('addContractor', lang)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('name', lang)} *</Label>
                  <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{t('company', lang)}</Label>
                  <Input value={form.company} onChange={e => setForm(p => ({...p, company: e.target.value}))} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Typ kontrahenta' : 'Type'}</Label>
                  <Select value={form.contractor_type || '__none__'} onValueChange={v => setForm(p => ({...p, contractor_type: v === '__none__' ? '' : v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {CONTRACTOR_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{lang === 'pl' ? ct.label_pl : ct.label_en}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Branża' : 'Industry'}</Label>
                  <Input value={form.industry} onChange={e => setForm(p => ({...p, industry: e.target.value}))} className="mt-1" placeholder="np. oświetlenie, HVAC" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Specjalizacja' : 'Specialization'}</Label>
                  <Input value={form.specialization} onChange={e => setForm(p => ({...p, specialization: e.target.value}))} className="mt-1" placeholder="np. architekt wnętrz" />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Miasto' : 'City'}</Label>
                  <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('email', lang)}</Label>
                  <Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{t('phone', lang)}</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('notes', lang)}</Label>
                <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1" />
              </div>
              <Button onClick={handleCreate} className="w-full">{t('create', lang)}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + filter bar */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={lang === 'pl' ? 'Szukaj po nazwie, firmie, specjalizacji...' : 'Search name, company, specialization...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-muted-foreground" /></button>}
        </div>

        {/* Type filter */}
        <Select value={filters.contractor_type || '__all__'} onValueChange={v => setFilters(p => ({...p, contractor_type: v === '__all__' ? null : v}))}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={lang === 'pl' ? 'Typ' : 'Type'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{lang === 'pl' ? 'Wszystkie typy' : 'All types'}</SelectItem>
            {CONTRACTOR_TYPES.map(ct => (
              <SelectItem key={ct.value} value={ct.value}>{lang === 'pl' ? ct.label_pl : ct.label_en}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Industry filter */}
        {filterOptions.industries.length > 0 && (
          <Select value={filters.industry || '__all__'} onValueChange={v => setFilters(p => ({...p, industry: v === '__all__' ? null : v}))}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={lang === 'pl' ? 'Branża' : 'Industry'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{lang === 'pl' ? 'Wszystkie branże' : 'All industries'}</SelectItem>
              {filterOptions.industries.map(ind => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* City filter */}
        {filterOptions.cities.length > 0 && (
          <Select value={filters.city || '__all__'} onValueChange={v => setFilters(p => ({...p, city: v === '__all__' ? null : v}))}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={lang === 'pl' ? 'Miasto' : 'City'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{lang === 'pl' ? 'Wszystkie miasta' : 'All cities'}</SelectItem>
              {filterOptions.cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Decision role filter */}
        {filterOptions.decision_roles.length > 0 && (
          <Select value={filters.decision_role || '__all__'} onValueChange={v => setFilters(p => ({...p, decision_role: v === '__all__' ? null : v}))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={lang === 'pl' ? 'Rola' : 'Role'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{lang === 'pl' ? 'Wszystkie role' : 'All roles'}</SelectItem>
              {filterOptions.decision_roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Active filters badges */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-xs">
            <X className="w-3 h-3" />{lang === 'pl' ? 'Wyczyść filtry' : 'Clear filters'}
          </Button>
        )}
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex gap-1 flex-wrap">
          {filters.contractor_type && (
            <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setFilters(p => ({...p, contractor_type: null}))}>
              {typeLabel(filters.contractor_type)} <X className="w-3 h-3" />
            </Badge>
          )}
          {filters.industry && (
            <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setFilters(p => ({...p, industry: null}))}>
              {filters.industry} <X className="w-3 h-3" />
            </Badge>
          )}
          {filters.city && (
            <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setFilters(p => ({...p, city: null}))}>
              <MapPin className="w-3 h-3" />{filters.city} <X className="w-3 h-3" />
            </Badge>
          )}
          {filters.decision_role && (
            <Badge variant="secondary" className="gap-1 cursor-pointer" onClick={() => setFilters(p => ({...p, decision_role: null}))}>
              {filters.decision_role} <X className="w-3 h-3" />
            </Badge>
          )}
        </div>
      )}

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : contractors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('noData', lang)}</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2 text-xs">
                  {lang === 'pl' ? 'Wyczyść filtry' : 'Clear filters'}
                </Button>
              )}
            </div>
          ) : (
            <Table data-testid="contractors-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('name', lang)}</TableHead>
                  <TableHead>{t('company', lang)}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Typ' : 'Type'}</TableHead>
                  <TableHead>{lang === 'pl' ? 'Miasto' : 'City'}</TableHead>
                  <TableHead>{t('reputation', lang)}</TableHead>
                  <TableHead className="text-center">{lang === 'pl' ? 'Negocjacje' : 'Negotiations'}</TableHead>
                  <TableHead className="text-right">{lang === 'pl' ? 'Akcje' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractors.map(c => (
                  <TableRow
                    key={c.id}
                    className="hover:bg-white/[0.02] transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/contractors/${c.id}`)}
                    title="Klik → szczegóły kontrahenta"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{c.name}</span>
                        {c.warning_count > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                      </div>
                      {c.specialization && <div className="text-xs text-muted-foreground">{c.specialization}</div>}
                      {/* IMPROV (2026-05-11): enriched row info — NIP + last activity */}
                      <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                        {c.nip && <span>NIP: {c.nip}</span>}
                        {c.last_activity_at && (
                          <span>Ostatnia aktywność: {new Date(c.last_activity_at).toLocaleDateString('pl-PL')}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.company}</TableCell>
                    <TableCell>
                      {c.contractor_type && (
                        <Badge variant="outline" className="text-xs">{typeLabel(c.contractor_type)}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.city}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${getReputationBg(c.reputation)}`}>
                        {Math.round((c.reputation || 0.5) * 100)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm">{c.negotiation_count || 0}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/contractors/${c.id}`); }}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
